import {
  sameAdvisoryPassage,
  type AdvisoryAnswerEvidenceValidation,
  type AdvisorySourceQuote,
  type AdvisorySourceReferenceV01,
} from './advisoryProvenance';
import type { AdvisoryQuoteReference } from './advisoryQuoteAnchors';
import type { AdvisoryTableSourceReference } from './advisoryTableSources';
import type { AdvisoryBriefQuestionInputV01 } from './types';

interface AdvisoryEvidenceIndex {
  evidence: AdvisoryAnswerEvidenceValidation | null;
  references: AdvisoryQuoteReference[];
  tableReferences: AdvisoryTableSourceReference[];
}

/** Normalized once per answer update and shared by every evidence view. */
export interface AdvisoryAnswerEvidenceEntry {
  input: AdvisoryBriefQuestionInputV01;
  evidence: AdvisoryAnswerEvidenceValidation | null;
}

const sameQuote = (a: AdvisorySourceQuote, b: AdvisorySourceQuote) =>
  a.text === b.text && a.status === b.status && a.segmentIndex === b.segmentIndex;

/** Selection keys describe the recorded passage, never its report position. */
function reportSourceKey(source: AdvisorySourceReferenceV01, answerKey: string): string {
  const identity = source.canonicalId
    ? ['canonical', source.canonicalId]
    : source.compositeId
    ? ['composite', source.compositeId]
    : source.documentId && source.chunkId
    ? ['document', source.documentId, source.chunkId]
    : // No shared identity: retain the contributing answer and local row, and
      // invalidate selection if a different record takes that row's position.
      ['unidentified', answerKey, source];
  return JSON.stringify(['report-source', source.kind, source.origin, identity]);
}

/**
 * One index entry per quotation, plus a full passage entry for explicit table citations.
 * `answerKeys` records which answers contributed the register, so a passage the
 * producer left unidentified can still name its chapters.
 */
export function separateAdvisorySourceEntries(
  evidence: AdvisoryAnswerEvidenceValidation | null,
  references: readonly AdvisoryQuoteReference[],
  answerKeys?: readonly string[],
  tableReferences: readonly AdvisoryTableSourceReference[] = []
): AdvisoryEvidenceIndex {
  if (!evidence?.ok) return { evidence, references: [...references], tableReferences: [] };
  const entries = evidence.value.sources.flatMap(source => {
    const scoped = answerKeys ? { ...source, answerKeys } : source;
    if (!scoped.quotes?.length) return [{ source: scoped, originalKey: source.key, quoteIndex: 0 }];
    return scoped.quotes.map((quote, quoteIndex) => ({
      source: {
        ...scoped,
        key: JSON.stringify([source.key, 'quote', quote.text, quote.status, quote.segmentIndex]),
        quotes: [quote],
      },
      originalKey: source.key,
      quoteIndex,
    }));
  });
  const mappedReferences = references.flatMap(reference => {
    const entry = entries.find(
      candidate =>
        candidate.originalKey === reference.source.key &&
        candidate.quoteIndex === reference.quoteIndex
    );
    return entry ? [{ ...reference, source: entry.source, quoteIndex: 0 }] : [];
  });
  const mappedTableReferences = tableReferences.map(reference => ({
    ...reference,
    source: {
      ...reference.source,
      // Index selections must use the same citation identity as inline badges.
      key: reference.key,
      ...(answerKeys ? { answerKeys } : {}),
    },
  }));
  // Follow the existing S-label order rather than clustering quotations by passage.
  const order = new Map(
    [...mappedReferences, ...mappedTableReferences].map((reference, index) => [
      reference.source.key,
      index,
    ])
  );
  const sources = [
    ...entries
      .filter(
        entry =>
          entry.source.quotes?.length ||
          !tableReferences.some(reference => reference.source.key === entry.originalKey)
      )
      .map(entry => entry.source),
    ...mappedTableReferences.map(reference => reference.source),
  ].sort(
    (a, b) =>
      (order.get(a.key) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.key) ?? Number.MAX_SAFE_INTEGER)
  );
  return {
    evidence: { ok: true, value: { ...evidence.value, sources } },
    references: mappedReferences,
    tableReferences: mappedTableReferences,
  };
}

/** Combine chapter registers without losing conflicting checks or their S labels. */
export function collectAdvisoryReportEvidence(
  answers: readonly AdvisoryAnswerEvidenceEntry[],
  references: Readonly<Record<string, readonly AdvisoryQuoteReference[]>>,
  tableReferences: Readonly<Record<string, readonly AdvisoryTableSourceReference[]>> = {}
): AdvisoryEvidenceIndex {
  const sources: AdvisorySourceReferenceV01[] = [];
  const combinedReferences: AdvisoryQuoteReference[] = [];
  const combinedTableReferences: AdvisoryTableSourceReference[] = [];
  // An unidentified passage never merges with another, so a passage comparison
  // cannot recover its chapters. Record them while the answer is still in hand.
  const contributors = new Map<AdvisorySourceReferenceV01, string[]>();
  let malformedSources = 0;
  let citationsOnWire: number | null = 0;
  const truncationFlags: (boolean | null)[] = [];
  let provided = false;
  for (const { input: answer, evidence: mapped } of answers) {
    if (mapped === null) {
      citationsOnWire = null;
      truncationFlags.push(null);
      continue;
    }
    provided = true;
    if (!mapped.ok) {
      malformedSources += 1;
      citationsOnWire = null;
      truncationFlags.push(null);
      continue;
    }
    const register = mapped.value;
    const answerKey = answer.answerKey || answer.questionId;
    malformedSources += register.malformedSources;
    citationsOnWire =
      citationsOnWire !== null && register.citationsOnWire !== null
        ? citationsOnWire + register.citationsOnWire
        : null;
    truncationFlags.push(register.truncated);
    for (const source of register.sources) {
      let merged = sources.find(candidate => sameAdvisoryPassage(candidate, source));
      if (!merged) {
        // `quotes` and the trim counters start empty for the same reason: they
        // are ACCUMULATED from every contributing answer below, this one
        // included, and a spread that carried them would count the first
        // answer twice.
        merged = {
          ...source,
          key: reportSourceKey(source, answerKey),
          quotes: [],
          quotesTruncated: false,
          quotesDropped: 0,
        };
        sources.push(merged);
        contributors.set(merged, []);
      }
      const tableReference = tableReferences[answerKey]?.find(
        reference => reference.source.key === source.key
      );
      if (
        tableReference &&
        !combinedTableReferences.some(reference => reference.source === merged)
      ) {
        combinedTableReferences.push({ ...tableReference, source: merged });
      }
      merged.quotesUnavailable ||= source.quotesUnavailable;
      // Two answers citing one passage: if either producer cut its anchor list,
      // the merged row is short and must say so. The counts add — the anchors
      // one answer lost are not the anchors the other lost.
      if (source.quotesTruncated) {
        merged.quotesTruncated = true;
        merged.quotesDropped = (merged.quotesDropped ?? 0) + (source.quotesDropped ?? 0);
      }
      const contributing = contributors.get(merged);
      if (contributing && !contributing.includes(answerKey)) contributing.push(answerKey);
      for (const { quote, localIndex } of (source.quotes ?? []).map((quote, localIndex) => ({
        quote,
        localIndex,
      }))) {
        const quotes = [...(merged.quotes ?? [])];
        let quoteIndex = quotes.findIndex(candidate => sameQuote(candidate, quote));
        if (quoteIndex < 0) {
          quoteIndex = quotes.length;
          quotes.push(quote);
          merged.quotes = quotes;
        }
        const reference = references[answerKey]?.find(
          candidate => candidate.source.key === source.key && candidate.quoteIndex === localIndex
        );
        if (
          reference &&
          !combinedReferences.some(
            candidate => candidate.source === merged && candidate.quoteIndex === quoteIndex
          )
        ) {
          combinedReferences.push({ ...reference, source: merged, quote, quoteIndex });
        }
      }
    }
  }
  const truncated = truncationFlags.includes(true)
    ? true
    : truncationFlags.includes(null)
    ? null
    : false;
  for (const source of sources) source.answerKeys = contributors.get(source) ?? [];
  return separateAdvisorySourceEntries(
    provided
      ? { ok: true, value: { sources, malformedSources, citationsOnWire, truncated } }
      : null,
    combinedReferences,
    undefined,
    combinedTableReferences
  );
}

/** A source badge describes only its recorded quotations, never the whole answer. */
export function advisorySourceCheck(
  source: AdvisorySourceReferenceV01
): AdvisorySourceQuote['status'] {
  const quotes = source.quotes ?? [];
  for (const status of ['absent', 'unchecked'] as const) {
    if (quotes.some(quote => quote.status === status)) return status;
  }
  if (source.quotesUnavailable || quotes.length === 0) return 'unknown';
  return quotes.some(quote => quote.status === 'unknown') ? 'unknown' : 'verified';
}
